import { Link, useLocation } from "react-router-dom";
import LogoMark from "./LogoMark";
import { useSite } from "@/context/SiteContext";
import { ROU_WAVE } from "./RoiChat";
import { CITIES, findCity } from "@/lib/cities";
import { findPost } from "@/lib/blog";
import { FLORIDA_NAV } from "@/lib/florida-nav";
import { OFFICES, OFFICE_CONTACT_LINE } from "@/lib/consent";

export default function Footer() {
  const { config } = useSite();
  const { pathname } = useLocation();
  const cityMatch = pathname.match(/^\/solar\/([a-z-]+)$/);
  const blogMatch = pathname.match(/^\/blog\/(solar-[a-z0-9-]+)$/);
  let city = null;
  if (cityMatch) {
    city = findCity(cityMatch[1]);
  } else if (blogMatch) {
    const post = findPost(blogMatch[1]);
    if (post) city = { name: post.city, county: post.county, mapQuery: post.mapQuery };
  }
  return (
    <footer className="site">
      <div className="wrap footerrow" style={{ alignItems: "center" }}>
        <Link to="/" className="brand" data-testid="footer-brand">
          <span className="brandmark">
            <LogoMark size={22} />
          </span>
          <span>
            SOLAR BLACK BELT
            <small>{config.brandLine}</small>
          </span>
        </Link>
        <div className="footer-roi" data-testid="footer-roi">
          <img src={ROU_WAVE} alt="Roi Solar, the energy ninja, waving hello" loading="lazy" />
          <p>
            <b>Roi Solar says:</b> “Bills are for reading once — savings are for enjoying every month. Catch me by the chat
            button, or the humans at <a href={config.phoneHref}>{config.phone}</a>.”
          </p>
        </div>
        {city && (
          <div style={{ width: "100%" }} data-testid="footer-city-map">
            <span className="eyebrow">SERVING {city.name.toUpperCase()} · {city.county.toUpperCase()}</span>
            <iframe
              title={`Map of ${city.name}, FL`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(city.mapQuery)}&output=embed`}
              className="city-map"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        )}
        <div className="footlinks" style={{ width: "100%" }}>
          <span>SERVICE AREAS</span>
          {CITIES.map((c) => (
            <Link key={c.slug} to={`/solar/${c.slug}`} data-testid={`footer-city-${c.slug}`}>
              {c.name}
            </Link>
          ))}
        </div>
        <div className="footlinks">
          <span>© 2026 {config.brand}</span>
          <a href={config.phoneHref} data-testid="footer-phone">
            {config.phone}
          </a>
          <Link to="/live-quote" data-testid="footer-live-quote">Free live quote</Link>
          <Link to="/gallery" data-testid="footer-gallery">Our work gallery</Link>
          <Link to="/services" data-testid="footer-services">All services</Link>
          <Link to="/business" data-testid="footer-business">For business</Link>
          <Link to="/homeowners" data-testid="footer-homeowners">For homeowners</Link>
          <Link to="/roof-visualizer" data-testid="footer-roof">See your roof</Link>
          <Link to="/careers" data-testid="footer-careers">Careers</Link>
          <Link to="/projects">Jobs we've done</Link>
          <Link to="/blog">Blog</Link>
          <Link to="/learn">Learn solar</Link>
          <Link to="/academy" data-testid="footer-academy">Solar Black Belt Academy</Link>
          <Link to="/about">About</Link>
          <Link to="/contact">Get a quote</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/accessibility">Accessibility</Link>
          <Link to="/#services">All solutions</Link>
          <Link to="/unsubscribe" data-testid="footer-unsubscribe">Unsubscribe</Link>
          <Link to="/admin">Admin</Link>
        </div>
        <div className="offices" style={{ width: "100%" }} data-testid="footer-offices">
          <span>COASTAL ENERGY - OUR OFFICES</span>
          <div className="offices-grid">
            {OFFICES.map((o) => (
              <a key={o.city} href={o.maps} target="_blank" rel="noreferrer" className="office" data-testid={`footer-office-${o.city.toLowerCase().replace(" ", "-")}`}>
                <b>{o.city}</b>
                <address>{o.address}</address>
                <span className="micro">Open in Google Maps ↗</span>
              </a>
            ))}
          </div>
          <p className="office-contact" data-testid="footer-office-contact">{OFFICE_CONTACT_LINE}</p>
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--teal)", width: "100%", textAlign: "center", paddingBottom: 6 }}>
          We install commercial solar — <Link to="/projects">see the jobs we've done</Link> — call <a href={config.phoneHref}>{config.phone}</a> for your quote.
          Commercial sales led by Lawrence Williams.
        </div>
        <div className="footlinks" style={{ width: "100%" }}>
          <span>FLORIDA COMMERCIAL SOLAR</span>
          <Link to="/florida-commercial-solar">Florida Commercial Solar ROI</Link>
          {FLORIDA_NAV.filter((g) => g.group === "regions" || g.group === "resources").flatMap((g) => g.items).map(([label, href]) => (
            <Link key={href} to={href}>{label}</Link>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "var(--muted)", width: "100%", textAlign: "center", paddingBottom: 6, lineHeight: 1.7 }} data-testid="footer-disclosures">
          Commercial Solar ROI is Coastal Energy's dedicated commercial solar platform — Coastal Energy performs the project
          services. Commercial sales are led by Lawrence Williams. Florida utility and tax guides carry a source-check date —
          account terms and current law control. No invented offices, reviews, credentials or tax promises.
        </div>
      </div>
    </footer>
  );
}
